import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, DateInput, SearchIcon, Switch } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, formatReportAmountAlways } from './shared/reportUtils';
import { buildReportQuery } from './shared/useAccountingReportFilters';

type Row = { lineId: number; levelCode: string; label: string; depth: number; amount: number };

type Response = { rows: Row[]; total: number; asOfDate: string };

export const BalanceSheet: React.FC = () => {
  const { colors } = useTheme();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [useLocalCurrency, setUseLocalCurrency] = useState(true);
  const [run, setRun] = useState(false);

  const reportUrl =
    run
      ? buildReportQuery('/accounting/reports/balance-sheet', {
          asOfDate,
          useLocalCurrency,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'label',
        header: 'Line',
        render: (v, row) => (
          <span style={{ paddingLeft: `${row.depth * 1.25}rem` }} className={row.depth === 0 ? 'font-semibold' : ''}>
            {v}
          </span>
        ),
      },
      { key: 'levelCode', header: 'Code', width: '9rem' },
      { key: 'amount', header: 'Balance', align: 'right', render: (v: number) => formatReportAmountAlways(v) },
    ],
    [],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div>
          <FieldLabel>As of date</FieldLabel>
          <DateInput value={asOfDate} onChange={(e) => { setAsOfDate(e.target.value); setRun(false); }} showFormatHint={false} />
        </div>
        <Switch label="Local currency" checked={useLocalCurrency} onChange={(e) => { setUseLocalCurrency(e.target.checked); setRun(false); }} size="sm" />
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Badge variant="secondary" className="h-10 px-3">All companies</Badge>
        <Button variant="default" onClick={() => setRun(true)} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Balance Sheet"
      exportFileBase="balance-sheet"
      exportDateTo={asOfDate}
      statusBadge={
        <Badge variant="info" className="font-normal">
          As of {formatDateGlobal(asOfDate)}
        </Badge>
      }
      statusExtra={
        <div className="flex items-center gap-2">
          <Link
            to="/accounting/statement-lines?type=BS"
            className="text-xs text-indigo-600 hover:underline whitespace-nowrap"
          >
            Configure lines
          </Link>
          <Badge variant="secondary" className="font-normal">
            {useLocalCurrency ? 'Local currency' : 'USD'}
          </Badge>
        </div>
      }
      recordCount={rows.length}
      readyMessage="Set as-of date and click Search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => String(r.lineId)}
        stickyHeader
        emptyMessage="No statement lines."
      />
      {data && (
        <div className="p-4 text-right font-semibold border-t">
          Total: {formatReportAmountAlways(data.total)}
        </div>
      )}
    </AccountingReportShell>
  );
};
